import React from 'react';

import {
  LSPaymentHardwareSetupStatus,
  useLightspeedPaymentsSetupStatus,
} from 'root/apps/hooks/useLightspeedPaymentsSetupStatus';
import { PrintedDocumentLanguages } from 'root/apps/PrintedDocumentLanguages';

import {
  DueAmountFormats,
  usePaymentTermsInputContext,
} from '../contexts/CreditStatementTermsContext';
import { OpenModalTypes } from '../types';
import { printCreditStatement } from '../utils';
import EmailModal from './EmailModal';
import PaymentTermsInput from './PaymentTermsInput';
import PreviewModal from './PreviewModal';

export const CREDIT_ACCOUNT_STATEMENT_FORM_URL = `/receipt.php?form_name=load_credit_account_statement`;

export default function PaymentTerms() {
  const [modalToOpenAfterLanguageSelection, setModalToOpenAfterLanguageSelection] =
    React.useState<OpenModalTypes>('none');

  const {
    globalState: {
      printDocumentLanguage,
      openModal,
      startDate,
      endDate,
      hasLightSpeedPayments,
      includePaymentLink,
    },
    constantProps: {
      shopId,
      registerId,
      employeeId,
      creditAccount: { id: creditAccountId },
    },
    setGlobalState,
    getDueAmount,
    getDueDate,
  } = usePaymentTermsInputContext();

  React.useEffect(() => {
    const handleDateChange = (event: Event) => {
      const { value, id } = event.target as HTMLInputElement;
      const targetType = id === 'view_custom_account_statement_start' ? 'startDate' : 'endDate';
      setGlobalState((prevState) => ({ ...prevState, [targetType]: value }));
    };

    const startDateInput = document.querySelector(
      '#print-account-statement-date-picker #view_custom_account_statement_start'
    );

    const endDateInput = document.querySelector(
      '#print-account-statement-date-picker #view_custom_account_statement_end'
    );

    startDateInput?.addEventListener('change', handleDateChange);
    endDateInput?.addEventListener('change', handleDateChange);

    // In some navigation flows (e.g. coming from the register "Edit customer" link),
    // legacy code auto-focuses the "Print from" date input on mount, which immediately
    // opens the calendar. To avoid the calendar popping open without an explicit user
    // action, blur the input if it was auto-focused during mount.
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && (active === startDateInput || active === endDateInput)) {
        active.blur();
      }
    }, 0);

    return () => {
      startDateInput?.removeEventListener('change', handleDateChange);
      endDateInput?.removeEventListener('change', handleDateChange);
    };
  }, [setGlobalState]);

  const { status: lightspeedPaymentsSetupStatus, isLoading } = useLightspeedPaymentsSetupStatus();

  React.useEffect(() => {
    setGlobalState((prevState) => ({
      ...prevState,
      hasLightSpeedPayments:
        lightspeedPaymentsSetupStatus ===
          LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED ||
        lightspeedPaymentsSetupStatus === LSPaymentHardwareSetupStatus.TRANSACTIONAL,
    }));
  }, [lightspeedPaymentsSetupStatus, setGlobalState]);

  const statementPeriodBalanceUrl = `${CREDIT_ACCOUNT_STATEMENT_FORM_URL}&credit_account_id=${creditAccountId}&start=${startDate}&end=${endDate}`;

  const printedDocumentLanguagesCallback = async (language: string) => {
    const dueAmountInCents = getDueAmount(DueAmountFormats.CENTS);
    const dueDate = getDueDate();
    // if there is no other modal that needs to be opened after language selection, then print the statement
    if (modalToOpenAfterLanguageSelection === 'none') {
      await printCreditStatement({
        balanceCheckUrl: statementPeriodBalanceUrl,
        creditAccountId,
        language,
        hasLightSpeedPayments,
        employeeId,
        registerId,
        shopId,
        dueAmountInCents,
        dueDate,
        includePaymentLink,
      });

      // After printing the document, reset the printDocumentLanguage
      setGlobalState((prevState) => ({
        ...prevState,
        openModal: modalToOpenAfterLanguageSelection,
        printDocumentLanguage: '',
      }));
    } else {
      setGlobalState((prevState) => ({
        ...prevState,
        openModal: modalToOpenAfterLanguageSelection,
        printDocumentLanguage: language,
      }));
    }
  };

  const onPrintedDocumentLanguageModalClose = () => {
    // Reset printDocumentLanguage selection and close all modals
    setGlobalState((prevState) => ({
      ...prevState,
      printDocumentLanguage: '',
      openModal: 'none',
    }));
  };

  const getButtonText = () => {
    if (modalToOpenAfterLanguageSelection === 'none') return 'Print';
    return modalToOpenAfterLanguageSelection.toCapitalize();
  };

  return (
    <>
      <PreviewModal openModalAfterLangSelection={setModalToOpenAfterLanguageSelection} />
      <PaymentTermsInput
        isLoadingHasLSP={isLoading}
        openModalAfterLangSelection={setModalToOpenAfterLanguageSelection}
      />
      {openModal === 'printDocumentLanguage' && printDocumentLanguage === '' ? (
        <PrintedDocumentLanguages
          callback={printedDocumentLanguagesCallback}
          onClose={onPrintedDocumentLanguageModalClose}
          buttonText={getButtonText()}
        />
      ) : null}
      {openModal === 'email' ? <EmailModal hasLightSpeedPayments={hasLightSpeedPayments} /> : null}
    </>
  );
}
